/**
 * Severity normaliser — frontend mirror of the back-and-forth between the
 * stored enum form and the human-readable labels the dropdown uses.
 *
 * Backend writes the enum form (`backend/routes/incidents.py`
 * `_normalise_severity`):
 *   near_miss · minor · moderate · major · catastrophic
 * Forms expose the friendly labels:
 *   Low · Medium · High · Critical
 *
 * Without normalisation the GET response value (`moderate`) does not match
 * the dropdown's `<option value="Medium">` — React picks the first option
 * (`Low`) every time, and the colour-coded badge falls into the default
 * grey case. Always run incident severity through `severityToLabel()` at
 * load + refresh; the backend accepts the friendly label on POST/PUT and
 * normalises back to the enum on the way in, so no reverse mapping is
 * needed on save.
 *
 * Single source of truth on the backend: `_STRING_TO_SEVERITY_ENUM` and
 * `_HAZARD_TYPE_TO_SEVERITY_ENUM` in `backend/routes/incidents.py`. Keep
 * this map in sync when those grow.
 */

const ENUM_TO_LABEL = {
  // Stored enum form from the backend
  near_miss: 'Near Miss',
  minor: 'Low',
  moderate: 'Medium',
  major: 'High',
  catastrophic: 'Critical',
  // Numeric severity_numeric (1-5) — best-effort fallback
  1: 'Low',
  2: 'Medium',
  3: 'High',
  4: 'Critical',
  // Already-label inputs pass through
  Low: 'Low',
  Medium: 'Medium',
  High: 'High',
  Critical: 'Critical',
  'Near Miss': 'Near Miss',
};

/** Convert a stored severity value to the dropdown / badge label. */
export function severityToLabel(value) {
  if (value === null || value === undefined || value === '') return '';
  // Lower-case strings hit the enum keys; numeric / already-cased values
  // hit their own keys directly. Fall through to the raw value if we don't
  // know it — better to render an unexpected label than to silently coerce
  // it to "Low".
  const direct = ENUM_TO_LABEL[value];
  if (direct) return direct;
  const lower = String(value).toLowerCase();
  return ENUM_TO_LABEL[lower] || String(value);
}

/**
 * Tailwind class string for the severity badge. Accepts either the stored
 * enum form or the friendly label so callers don't need to know which
 * shape they have in hand.
 */
export function severityBadgeClass(value) {
  switch (severityToLabel(value)) {
    case 'Critical':
      return 'bg-red-200 text-red-800 border-red-600';
    case 'High':
      return 'bg-red-100 text-red-700 border-red-500';
    case 'Medium':
      return 'bg-yellow-100 text-yellow-700 border-yellow-500';
    case 'Low':
      return 'bg-green-100 text-green-700 border-green-500';
    case 'Near Miss':
      return 'bg-blue-100 text-blue-700 border-blue-500';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-500';
  }
}
